// Моделът на „История на промените" — чист, без React. От отговора на
// `GET /inventory/documents/{id}/revisions` строи записите за показване: всяка
// снимка се сравнява със СЛЕДВАЩАТА (…, последна снимка → текущ документ), а
// разликата се приписва на човека и момента на снимката (тя е състоянието ПРЕДИ
// неговата промяна). Най-новите са първи, „създаден" е последен.
//
// Сравняват се САМО въведените стойности — дата, номер, доставчик, склад(ове),
// вид, бележка/причина, плащане, количество, ръчна цена, партида, срок. FIFO
// себестойността, очакваното при инвентаризация и „по фактура" на стокова се
// пренаписват и без редакция (replay / друг документ) и биха приписали чужда
// промяна на редактора. Имената се показват, но сравнението е по id.
//
// Редовете се сдвояват по стабилен ключ: първо точно (продукт + склад +
// партида), после свободно (продукт + склад) — сменена партида е промяна на
// реда, не „премахнат + добавен". Какво точно се сравнява във всеки от
// четирите документа — `revisionDocumentSpecs.ts`.
import type {
  DocumentRevisions,
  RevisionMovementType,
  RevisionSnapshot,
} from "../../../../api/inventoryRevisions";
import { DOCUMENT_SPECS } from "./revisionDocumentSpecs";

export type RevisionDocumentKind = "delivery" | "writeoff" | "stocktake" | "transfer";

// Изписване и бракуване са един документ (видът е поле в него)
export const revisionKindOf = (movementType: RevisionMovementType | string): RevisionDocumentKind =>
  movementType === "scrap" || movementType === "writeoff"
    ? "writeoff"
    : movementType === "stocktake" || movementType === "transfer"
      ? movementType
      : "delivery";

export type Translate = (key: string, options?: Record<string, unknown>) => string;

// Какво трябва на модела отвън: превод и мерната единица на продукта.
export type RevisionDiffContext = {
  t: Translate;
  decimalsOf: (productId?: number | null) => number;
  unitOf: (productId: number) => string | null | undefined;
  // име на продукт извън редовете (замените на инвентаризацията носят само id)
  productNameOf?: (productId: number) => string | null | undefined;
};

// Една сменена стойност — вече форматирана за показване; null = празно („—").
export type RevisionFieldChange = {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
};

export type RevisionLineChange = {
  key: string;
  kind: "added" | "removed" | "changed";
  name: string;
  // склад (разпределена доставка), „Рецепта", „Замяна" — или нищо
  context: string | null;
  // етикет пред основната стойност („Намерено") — или нищо (к-во × цена)
  mainLabel: string | null;
  before: string | null; // основната стойност преди (null при добавен)
  after: string | null; // основната стойност след (null при премахнат)
  mainChanged: boolean;
  details: RevisionFieldChange[]; // партида, срок, цена, нето/бруто…
};

export type RevisionChangeSet = {
  header: RevisionFieldChange[];
  lines: RevisionLineChange[];
  // общите суми — за контекст, НЕ се броят като промяна
  totals: RevisionFieldChange[];
  count: number;
};

export type RevisionEntry =
  | {
      type: "edit";
      id: string;
      revisionNo: number;
      changedAt: string;
      changedByName: string | null;
      changes: RevisionChangeSet;
    }
  | { type: "created"; id: string; createdAt: string | null; createdByName: string | null };

// ─── Спецификацията на документ (попълва се в revisionDocumentSpecs.ts) ───────
export type HeaderFieldSpec = {
  field: string;
  label: (ctx: RevisionDiffContext, before: RevisionSnapshot, after: RevisionSnapshot) => string;
  key: (snapshot: RevisionSnapshot) => string; // сравнимата стойност
  display: (snapshot: RevisionSnapshot, ctx: RevisionDiffContext) => string | null;
  // полето не се сравнява за тази двойка (напр. плащане на стокова във фактура)
  skip?: (before: RevisionSnapshot, after: RevisionSnapshot) => boolean;
};

export type LineDetail = { field: string; label: string; key: string; display: string | null };

// Ред на документа, сведен до сравними части.
export type LineItem = {
  key: string; // свободен ключ (продукт + склад)
  exactKey: string; // точен ключ (+ партида)
  name: string;
  context: string | null;
  mainLabel: string | null;
  summary: string | null;
  mainKey: string;
  details: LineDetail[];
};

export type DocumentSpec = {
  header: HeaderFieldSpec[];
  totals: HeaderFieldSpec[];
  lines: (
    snapshot: RevisionSnapshot,
    ctx: RevisionDiffContext,
    pair: { before: RevisionSnapshot; after: RevisionSnapshot },
  ) => LineItem[];
};

// ─── Сравнение ────────────────────────────────────────────────────────────────
const fieldChanges = (
  specs: HeaderFieldSpec[],
  before: RevisionSnapshot,
  after: RevisionSnapshot,
  ctx: RevisionDiffContext,
): RevisionFieldChange[] =>
  specs.flatMap((spec) => {
    if (spec.skip?.(before, after) || spec.key(before) === spec.key(after)) return [];
    return [
      {
        field: spec.field,
        label: spec.label(ctx, before, after),
        before: spec.display(before, ctx),
        after: spec.display(after, ctx),
      },
    ];
  });

type LinePair = { before: LineItem | null; after: LineItem | null };

// Сдвояване на редовете: точен ключ, после свободен; останалите от преди са
// премахнати (добавят се накрая), несдвоените от после — добавени.
export const matchLines = (before: LineItem[], after: LineItem[]): LinePair[] => {
  const unmatched = [...before];
  const take = (matches: (item: LineItem) => boolean): LineItem | null => {
    const index = unmatched.findIndex(matches);
    return index < 0 ? null : unmatched.splice(index, 1)[0];
  };
  const pairs: LinePair[] = after.map((item) => ({ before: null, after: item }));
  for (const pair of pairs) pair.before = take((item) => item.exactKey === pair.after!.exactKey);
  for (const pair of pairs) {
    if (!pair.before) pair.before = take((item) => item.key === pair.after!.key);
  }
  return [...pairs, ...unmatched.map((item) => ({ before: item, after: null }))];
};

const lineChange = ({ before, after }: LinePair): RevisionLineChange | null => {
  if (before && after) {
    const mainChanged = before.mainKey !== after.mainKey;
    const details = after.details.flatMap((detail) => {
      const old = before.details.find((candidate) => candidate.field === detail.field);
      if (!old || old.key === detail.key) return [];
      return [{ field: detail.field, label: detail.label, before: old.display, after: detail.display }];
    });
    if (!mainChanged && details.length === 0) return null;
    return {
      key: after.key,
      kind: "changed",
      name: after.name,
      context: after.context,
      mainLabel: after.mainLabel,
      before: before.summary,
      after: after.summary,
      mainChanged,
      details,
    };
  }
  const item = (after ?? before) as LineItem;
  return {
    key: item.key,
    kind: after ? "added" : "removed",
    name: item.name,
    context: item.context,
    mainLabel: item.mainLabel,
    before: after ? null : item.summary,
    after: after ? item.summary : null,
    mainChanged: true,
    details: [],
  };
};

// Разликата между две съседни състояния на документ.
export const diffSnapshots = (
  kind: RevisionDocumentKind,
  before: RevisionSnapshot,
  after: RevisionSnapshot,
  ctx: RevisionDiffContext,
): RevisionChangeSet => {
  const spec = DOCUMENT_SPECS[kind];
  const pair = { before, after };
  const header = fieldChanges(spec.header, before, after, ctx);
  const lines = matchLines(spec.lines(before, ctx, pair), spec.lines(after, ctx, pair))
    .map(lineChange)
    .filter((change): change is RevisionLineChange => change !== null);
  const count = header.length + lines.length;
  const totals = count > 0 ? fieldChanges(spec.totals, before, after, ctx) : [];
  return { header, lines, totals, count };
};

// „28.09 14:32" в текущата година, „28.09.2025 14:32" в друга; без момент → „—".
export const formatRevisionWhen = (iso: string | null, now: Date = new Date()): string => {
  if (!iso) return "—";
  const moment = new Date(iso);
  if (Number.isNaN(moment.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, "0");
  const dayMonth = `${pad(moment.getDate())}.${pad(moment.getMonth() + 1)}`;
  const year = moment.getFullYear() === now.getFullYear() ? "" : `.${moment.getFullYear()}`;
  return `${dayMonth}${year} ${pad(moment.getHours())}:${pad(moment.getMinutes())}`;
};

// Записите за показване: редакциите от най-новата към най-старата, накрая
// „създаден". Редакция k = снимка k → снимка k+1 (последната → `current`).
export const buildRevisionEntries = (
  data: DocumentRevisions,
  ctx: RevisionDiffContext,
): RevisionEntry[] => {
  const kind = revisionKindOf(data.movementType);
  const states = [...data.revisions.map((revision) => revision.snapshot), data.current];
  const edits: RevisionEntry[] = data.revisions.map((revision, index) => ({
    type: "edit",
    id: `rev-${revision.revisionNo}-${index}`,
    revisionNo: revision.revisionNo,
    changedAt: revision.changedAt,
    changedByName: revision.changedByName,
    changes: diffSnapshots(kind, states[index], states[index + 1], ctx),
  }));
  return [
    ...edits.reverse(),
    { type: "created", id: "created", createdAt: data.createdAt, createdByName: data.createdByName },
  ];
};
